/**
 * Module 6C.3 — Product Tour.
 *
 * Homegrown step-sequence with two render modes:
 *   1. Modal Dialog (welcome + finish steps — no DOM anchor)
 *   2. Popover anchored to an existing `data-testid` element (mid steps)
 *
 * Zero new dependencies. Reuses shadcn Dialog + Popover primitives.
 *
 * The tour:
 *   - shows once per user (gated on `shouldShowTour(state)`)
 *   - is dismissible at every step (X button + Esc + backdrop click)
 *   - stamps `tour_completed_at` on the last step's "Done" button
 *   - is re-triggerable via Settings → "Replay tour"
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

import { completeTour, dismissOnboarding } from "@/lib/onboarding";


/**
 * Each step is anchored to an existing data-testid (the nav items and the
 * brand kit card already have these from earlier modules — we don't add
 * new attributes to the DOM, we reuse what's there).
 */
const STEPS = [
  {
    id: "welcome",
    anchor: null,           // null → modal
    title: (studioName) => `Welcome to ${studioName || "SnapOps AI"}`,
    body: "A quick 90-second tour of what's where. You can skip any time.",
  },
  {
    id: "pipeline",
    anchor: '[data-testid="nav-leads"]',
    title: "Your Pipeline",
    body: "Every lead lands here, grouped by stage. Drag cards between columns as conversations progress.",
  },
  {
    id: "dashboard",
    anchor: '[data-testid="nav-dashboard"]',
    title: "Numbers at a glance",
    body: "Live KPIs, conversion, pipeline value — Dashboard updates as real leads come in.",
  },
  {
    id: "today",
    anchor: '[data-testid="nav-today"]',
    title: "Your daily queue",
    body: "Follow-ups due today plus operational tasks (vendor confirms, balance reminders) — your morning routine.",
  },
  {
    id: "brand",
    anchor: '[data-testid="nav-settings"]',
    title: "Make it yours",
    body: "Settings holds Brand Kit (logo, color, typography) and team management. Your studio, your colors.",
  },
  {
    id: "finish",
    anchor: null,
    title: "You're set",
    body: "Two sample leads are waiting in your Pipeline so the page isn't empty. Delete them when you're ready to go live.",
  },
];


function getAnchorRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}


function PopoverStep({ step, index, total, onNext, onPrev, onDismiss }) {
  // We compute the popover's screen position from the anchor's bounding
  // rect on every render. Anchored to the RIGHT of the sidebar item.
  const [rect, setRect] = useState(() => getAnchorRect(step.anchor));

  useEffect(() => {
    setRect(getAnchorRect(step.anchor));
    const handle = () => setRect(getAnchorRect(step.anchor));
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [step.anchor]);

  // Anchor not found — likely the user is on a page where the sidebar
  // item is hidden (RBAC). Fall back to dismissing this step silently.
  if (!rect) {
    return (
      <div
        className="fixed inset-0 z-[60] bg-transparent"
        onClick={onNext}
        data-testid={`tour-step-${step.id}-anchor-missing`}
      />
    );
  }

  // Position to the right of the anchor with an 18px gap. Pinned to top
  // edge so it doesn't shift when steps have different copy lengths.
  const top = Math.max(16, rect.top);
  const left = rect.right + 18;

  return (
    <>
      {/* Backdrop — semi-transparent, click dismisses */}
      <div
        className="fixed inset-0 z-[55] bg-black/30"
        onClick={onDismiss}
        data-testid="tour-backdrop"
      />
      {/* Spotlight ring around the anchor */}
      <div
        className="fixed z-[56] pointer-events-none"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          borderRadius: 6,
          boxShadow: "0 0 0 3px var(--brand-primary), 0 0 0 9999px rgba(0,0,0,0)",
          background: "transparent",
        }}
      />
      {/* Popover card */}
      <div
        className="fixed z-[60] bg-white shadow-2xl"
        style={{
          top,
          left,
          width: 320,
          borderTop: "3px solid var(--brand-primary)",
          border: "1px solid var(--brand-border)",
        }}
        data-testid={`tour-step-${step.id}`}
        role="dialog"
        aria-labelledby={`tour-step-${step.id}-title`}
      >
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">
            Step {index + 1} of {total}
          </div>
          <button
            onClick={onDismiss}
            className="text-brand-muted hover:text-brand-ink"
            data-testid="tour-dismiss-btn"
            aria-label="Skip tour"
          >
            <X size={14} />
          </button>
        </div>
        <div className="px-4 pb-3">
          <h3
            id={`tour-step-${step.id}-title`}
            className="font-display text-lg mb-2"
            style={{ color: "var(--brand-ink)" }}
          >
            {step.title}
          </h3>
          <p className="text-sm text-brand-muted leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center justify-between border-t border-brand-border px-4 py-3">
          <button
            onClick={onPrev}
            disabled={index === 0}
            className="text-xs tracking-[0.14em] uppercase text-brand-muted hover:text-brand-ink disabled:opacity-30 disabled:hover:text-brand-muted inline-flex items-center gap-1"
            data-testid="tour-prev-btn"
          >
            <ArrowLeft size={12} /> Back
          </button>
          <button
            onClick={onNext}
            className="text-xs tracking-[0.14em] uppercase text-white px-3 py-1.5 inline-flex items-center gap-1"
            style={{ background: "var(--brand-primary)" }}
            data-testid="tour-next-btn"
          >
            Next <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </>
  );
}


function ModalStep({ step, index, total, isLast, onNext, onPrev, onDismiss, onComplete }) {
  return (
    <>
      <div
        className="fixed inset-0 z-[55] bg-black/40"
        onClick={onDismiss}
        data-testid="tour-backdrop"
      />
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white shadow-2xl"
        style={{
          width: 460,
          maxWidth: "90vw",
          borderTop: "3px solid var(--brand-primary)",
          border: "1px solid var(--brand-border)",
        }}
        data-testid={`tour-step-${step.id}`}
        role="dialog"
        aria-labelledby={`tour-step-${step.id}-title`}
      >
        <div className="flex items-start justify-between p-6 pb-3">
          <div className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">
            Step {index + 1} of {total}
          </div>
          <button
            onClick={onDismiss}
            className="text-brand-muted hover:text-brand-ink"
            data-testid="tour-dismiss-btn"
            aria-label="Skip tour"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 pb-4">
          <h2
            id={`tour-step-${step.id}-title`}
            className="font-display text-2xl mb-3"
            style={{ color: "var(--brand-ink)" }}
          >
            {step.title}
          </h2>
          <p className="text-sm text-brand-muted leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center justify-between border-t border-brand-border px-6 py-4">
          <button
            onClick={onPrev}
            disabled={index === 0}
            className="text-xs tracking-[0.14em] uppercase text-brand-muted hover:text-brand-ink disabled:opacity-30 disabled:hover:text-brand-muted inline-flex items-center gap-1"
            data-testid="tour-prev-btn"
          >
            <ArrowLeft size={12} /> Back
          </button>
          {isLast ? (
            <button
              onClick={onComplete}
              className="text-xs tracking-[0.14em] uppercase text-white px-4 py-2"
              style={{ background: "var(--brand-primary)" }}
              data-testid="tour-done-btn"
            >
              Done
            </button>
          ) : (
            <button
              onClick={onNext}
              className="text-xs tracking-[0.14em] uppercase text-white px-3 py-1.5 inline-flex items-center gap-1"
              style={{ background: "var(--brand-primary)" }}
              data-testid="tour-next-btn"
            >
              Next <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}


export default function ProductTour({ open, onClose, onCompleted, studioName }) {
  // `open` is parent-controlled. Parent unmounts when the user dismisses
  // OR completes the tour, AFTER refreshing onboarding state from the
  // server. We don't keep local "shown" state — single source of truth.
  const [index, setIndex] = useState(0);

  // Esc handler — same effect as dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = async (e) => {
      if (e.key === "Escape") {
        await handleDismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const rawStep = STEPS[index];
  // Module 6J v2 — welcome step is personalized with the studio's name
  // (falls back to "SnapOps AI"). Only the welcome step defines `title`
  // as a function; all other steps keep it as a plain string.
  const step = typeof rawStep.title === "function"
    ? { ...rawStep, title: rawStep.title(studioName) }
    : rawStep;
  const isLast = index === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) return;
    setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const handlePrev = () => setIndex((i) => Math.max(i - 1, 0));

  const handleDismiss = async () => {
    try {
      await dismissOnboarding("tour");
    } catch {}
    setIndex(0);
    onClose && onClose();
  };
  const handleComplete = async () => {
    try {
      await completeTour();
    } catch {}
    setIndex(0);
    onCompleted && onCompleted();
    onClose && onClose();
  };

  const node = step.anchor ? (
    <PopoverStep
      step={step}
      index={index}
      total={STEPS.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onDismiss={handleDismiss}
    />
  ) : (
    <ModalStep
      step={step}
      index={index}
      total={STEPS.length}
      isLast={isLast}
      onNext={handleNext}
      onPrev={handlePrev}
      onDismiss={handleDismiss}
      onComplete={handleComplete}
    />
  );

  // Portal to body so the tour overlays everything, including the sidebar.
  return createPortal(node, document.body);
}
